<div class="login-wrapper">
  <div class="login reset-password-container">
    <section class="title">
      <h3 class="welcome">{{ 'Passwort zurücksetzen' | mrTranslate }}</h3>
      <img
        src="assets/mrprocloudlogo{{ !isDarkThemeAktiv ? '-dark' : '' }}.svg"
        class="mw-100-p"
        style="max-height: 120px" />
    </section>

    <!-- Loading State -->
    @if (currentStep === 'loading') {
      <div class="step-container">
        <div class="loading-spinner">
          <clr-spinner [clrMedium]="true">{{ 'Überprüfe Link...' | mrTranslate }}</clr-spinner>
        </div>
        <p class="text-center mt-1">{{ 'Link wird überprüft...' | mrTranslate }}</p>
      </div>
    }

    <!-- Invalid Token State -->
    @if (currentStep === 'invalid-token') {
      <div class="step-container">
        <clr-alert [clrAlertType]="'danger'" [clrAlertClosable]="false">
          <clr-alert-item>
            <span class="alert-text">
              {{ 'Der Link ist ungültig oder abgelaufen.' | mrTranslate }}
            </span>
          </clr-alert-item>
        </clr-alert>
        <p class="mt-1">
          {{ 'Bitte fordern Sie einen neuen Link an, um Ihr Passwort zurückzusetzen.' | mrTranslate }}
        </p>
        <div class="btn-group mt-1">
          <button class="btn btn-primary" (click)="requestNewLink()">
            {{ 'Neuen Link anfordern' | mrTranslate }}
          </button>
          <button class="btn" (click)="goToLogin()">
            {{ 'Zurück zum Login' | mrTranslate }}
          </button>
        </div>
      </div>
    }

    <!-- PIN Entry Step -->
    @if (currentStep === 'enter-pin') {
      <div class="step-container">
        <clr-alert [clrAlertType]="'info'" [clrAlertClosable]="false">
          <clr-alert-item>
            <span class="alert-text">
              {{ 'Geben Sie den 6-stelligen PIN ein, den Sie per E-Mail erhalten haben.' | mrTranslate }}
            </span>
          </clr-alert-item>
        </clr-alert>

        @if (pinHint) {
          <p class="pin-hint">
            {{ 'PIN-Hinweis:' | mrTranslate }}
            <strong>{{ pinHint }}</strong>
          </p>
        }

        <form clrForm clrLayout="vertical" class="pin-form">
          <div class="pin-input-container">
            <label>{{ 'Bestätigungscode' | mrTranslate }}</label>
            <code-input
              [codeLength]="6"
              [isCodeHidden]="false"
              [initialFocusField]="0"
              (codeCompleted)="onPinCodeCompleted($event)"
              (codeChanged)="onPinCodeChanged($event)"></code-input>
          </div>

          @if (attemptsRemaining !== null && attemptsRemaining > 0) {
            <p class="attempts-warning">{{ 'Verbleibende Versuche:' | mrTranslate }} {{ attemptsRemaining }}</p>
          }

          @if (timeRemaining) {
            <div class="timer-display">
              <clr-icon shape="clock"></clr-icon>
              <span>{{ 'Gültig für:' | mrTranslate }} {{ timeRemaining }}</span>
            </div>
          }

          <div class="btn-group mt-1">
            <button
              type="button"
              class="btn btn-primary"
              (click)="verifyPin()"
              [disabled]="isPinLoading || pinValue.length !== 6">
              @if (isPinLoading) {
                <clr-spinner [clrInline]="true"></clr-spinner>
              }
              {{ 'Bestätigen' | mrTranslate }}
            </button>
            <button type="button" class="btn" (click)="goToLogin()">
              {{ 'Abbrechen' | mrTranslate }}
            </button>
          </div>
        </form>
      </div>
    }

    <!-- Password Entry Step -->
    @if (currentStep === 'enter-password') {
      <div class="step-container">
        <clr-alert [clrAlertType]="'success'" [clrAlertClosable]="false">
          <clr-alert-item>
            <span class="alert-text">
              {{ 'PIN bestätigt! Bitte geben Sie Ihr neues Passwort ein.' | mrTranslate }}
            </span>
          </clr-alert-item>
        </clr-alert>

        <form clrForm [formGroup]="passwordForm" clrLayout="vertical" class="password-form">
          <clr-password-container>
            <label>{{ 'Neues Passwort' | mrTranslate }}</label>
            <input
              clrPassword
              formControlName="newPassword"
              (input)="checkPasswordStrength()"
              placeholder="{{ 'Neues Passwort eingeben' | mrTranslate }}" />
            <clr-control-error *clrIfError="'required'">
              {{ 'Das Feld muss ausgefüllt werden' | mrTranslate }}
            </clr-control-error>
            <clr-control-error *clrIfError="'minlength'">
              {{ 'Passwort muss mindestens 6 Zeichen lang sein' | mrTranslate }}
            </clr-control-error>
          </clr-password-container>

          <clr-password-container>
            <label>{{ 'Passwort bestätigen' | mrTranslate }}</label>
            <input
              clrPassword
              formControlName="confirmPassword"
              placeholder="{{ 'Passwort wiederholen' | mrTranslate }}"
              [class.is-invalid]="passwordMismatch" />
            <clr-control-error *clrIfError="'required'">
              {{ 'Das Feld muss ausgefüllt werden' | mrTranslate }}
            </clr-control-error>
          </clr-password-container>

          @if (passwordMismatch) {
            <clr-alert [clrAlertType]="'danger'" [clrAlertClosable]="false" [clrAlertSizeSmall]="true">
              <clr-alert-item>
                <span class="alert-text">{{ 'Die Passwörter stimmen nicht überein.' | mrTranslate }}</span>
              </clr-alert-item>
            </clr-alert>
          }

          <!-- Password Requirements -->
          <div class="password-requirements">
            <p class="requirements-title">{{ 'Passwortanforderungen' | mrTranslate }}</p>
            <ul class="requirements-list">
              <li [class.valid]="hasMinLength">
                <clr-icon shape="check" size="14"></clr-icon>
                {{ 'Mindestens 8 Zeichen' | mrTranslate }}
              </li>
              <li [class.valid]="hasUpperCase">
                <clr-icon shape="check" size="14"></clr-icon>
                {{ 'Großbuchstabe (A-Z)' | mrTranslate }}
              </li>
              <li [class.valid]="hasLowerCase">
                <clr-icon shape="check" size="14"></clr-icon>
                {{ 'Kleinbuchstabe (a-z)' | mrTranslate }}
              </li>
              <li [class.valid]="hasNumber">
                <clr-icon shape="check" size="14"></clr-icon>
                {{ 'Zahl (0-9)' | mrTranslate }}
              </li>
              <li [class.valid]="hasSpecialChar">
                <clr-icon shape="check" size="14"></clr-icon>
                {{ 'Sonderzeichen' | mrTranslate }}
              </li>
            </ul>
          </div>

          <div class="btn-group mt-1">
            <button
              type="button"
              class="btn btn-primary"
              (click)="completeReset()"
              [disabled]="isPasswordLoading || !passwordForm.valid || !isPasswordValid">
              @if (isPasswordLoading) {
                <clr-spinner [clrInline]="true"></clr-spinner>
              }
              {{ 'Passwort ändern' | mrTranslate }}
            </button>
            <button type="button" class="btn" (click)="goToLogin()">
              {{ 'Abbrechen' | mrTranslate }}
            </button>
          </div>
        </form>
      </div>
    }

    <!-- Success State -->
    @if (currentStep === 'success') {
      <div class="step-container success-container">
        <clr-icon shape="success-standard" size="72" class="success-icon"></clr-icon>
        <h4>{{ 'Passwort erfolgreich geändert!' | mrTranslate }}</h4>
        <p>{{ 'Sie können sich jetzt mit Ihrem neuen Passwort anmelden.' | mrTranslate }}</p>
        <button class="btn btn-primary" (click)="goToLogin()">
          {{ 'Zum Login' | mrTranslate }}
        </button>
      </div>
    }

    <!-- Error State -->
    @if (currentStep === 'error') {
      <div class="step-container">
        <clr-alert [clrAlertType]="'danger'" [clrAlertClosable]="false">
          <clr-alert-item>
            <span class="alert-text">
              {{ 'Ein Fehler ist aufgetreten.' | mrTranslate }}
            </span>
          </clr-alert-item>
        </clr-alert>
        <p class="mt-1">
          {{ 'Bitte versuchen Sie es erneut oder fordern Sie einen neuen Link an.' | mrTranslate }}
        </p>
        <div class="btn-group mt-1">
          <button class="btn btn-primary" (click)="requestNewLink()">
            {{ 'Neuen Link anfordern' | mrTranslate }}
          </button>
          <button class="btn" (click)="goToLogin()">
            {{ 'Zurück zum Login' | mrTranslate }}
          </button>
        </div>
      </div>
    }
  </div>
</div>
